import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { useDebounce } from '@react-hook/debounce';
import {
    Button,
    Divider,
    Input,
    Modal,
    notification,
    Select,
    Switch,
    Tag,
    Typography,
} from 'antd';
import type { ColumnType, Key, SortOrder } from 'antd/lib/table/interface';
import { GenericOverviewTable } from 'components/GenericOverviewTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { deleteDestination, getAllDestinations } from 'api/destinations.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import {
    DestinationsListState,
    useDestinationsListState,
} from 'hooks/use-destinations-list-state';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import { DestinationListImage } from './DestinationListImage';
import styles from './style.module.css';
import { DestinationStatuses } from 'constants/destination-statuses';

const { Search } = Input;

export function DestinationsOverview() {
    const {
        query,
        page,
        sortColumn,
        sortOrder,
        onlyWithoutPhotos,
        hasGuide,
        source,
        status,
        returnToUrl,
        updateState,
    } = useDestinationsListState();
    const [debouncedQuery, setDebouncedQuery] = useDebounce(query, 300);

    const [isDeleting, setIsDeleting] = useState(false);
    const filterWithEnoughCharacters =
        debouncedQuery.length > 2 ? debouncedQuery : '';

    // Memoize the API parameters to prevent unnecessary re-renders
    const apiParams = useMemo(
        () => ({
            query: filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            status,
            source,
            sortColumn,
            sortOrder,
        }),
        [
            filterWithEnoughCharacters,
            onlyWithoutPhotos,
            hasGuide,
            source,
            status,
            sortColumn,
            sortOrder,
        ],
    );

    const apiGetter = useCallback(
        (pageNumber: number) => {
            if (pageNumber !== page) {
                updateState({ page: pageNumber });
            }

            return getAllDestinations({
                ...apiParams,
                page: pageNumber,
            });
        },
        [apiParams, page, updateState],
    );

    const handleDeleteDestination = useCallback(
        async (destinationId: number) => {
            setIsDeleting(true);
            try {
                await deleteDestination(destinationId);
                await apiGetter(page);
            } catch (error) {
                notification.error({
                    message: 'Error deleting destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            } finally {
                setIsDeleting(false);
            }
        },
        [apiGetter, page],
    );

    const handleShowDeleteConfirmation = useCallback(
        (destination: DestinationSchema) => {
            Modal.confirm({
                type: 'warning',
                title: 'Delete destination?',
                content: 'Are you sure you want to delete this destination?',
                onOk: () => {
                    handleDeleteDestination(destination.id);
                },
                okText: 'Delete',
                okButtonProps: {
                    danger: true,
                },
                cancelText: 'Cancel',
            });
        },
        [handleDeleteDestination],
    );

    const columns = useMemo<ColumnType<DestinationSchema>[]>(
        () => [
            {
                title: 'Image',
                dataIndex: 'image_url',
                key: 'cms_media_id',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <DestinationListImage
                        destination={destination}
                        onUpdate={() => apiGetter(page)} // Triggering the update of the table
                    />
                ),
                sorter: false,
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'locality',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Link
                        to={`/destinations/${destination.id}?tab=basicInfo`}
                        state={{
                            returnTo: returnToUrl,
                        }}
                    >
                        {destination.name || destination.locality}
                    </Link>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'locality',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Country',
                dataIndex: 'country',
                key: 'country',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Link
                        to={`/destinations/${destination.id}?tab=basicInfo`}
                        state={{
                            returnTo: returnToUrl,
                        }}
                    >
                        {destination.country}
                    </Link>
                ),
                sorter: true,
                sortOrder: getGenericTableSortOrder({
                    column: 'country',
                    currentSortColumn: sortColumn,
                    sortOrder,
                }),
            },
            {
                title: 'Content source',
                dataIndex: 'content_source',
                key: 'content_source',
                render: (_text: unknown, destination: DestinationSchema) =>
                    getContentSource(destination),
                sorter: false,
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (_text: unknown, destination: DestinationSchema) =>
                    destination.status === DestinationStatuses.PUBLISHED ? (
                        <Tag color="green">Published</Tag>
                    ) : (
                        <Tag color="blue">Draft</Tag>
                    ),
                sorter: false,
            },
            {
                title: 'Actions',
                dataIndex: 'actions',
                key: 'actions',
                render: (_text: unknown, destination: DestinationSchema) => (
                    <Button
                        size="middle"
                        icon={<DeleteOutlined />}
                        onClick={() =>
                            handleShowDeleteConfirmation(destination)
                        }
                        loading={isDeleting}
                        danger
                    />
                ),
                sorter: false,
            },
        ],
        [
            apiGetter,
            handleShowDeleteConfirmation,
            isDeleting,
            page,
            returnToUrl,
            sortColumn,
            sortOrder,
        ],
    );

    const handleSort = useCallback(
        (column: Key | undefined, order: SortOrder | undefined) => {
            updateState({
                sortColumn: column ? column.toString() : undefined,
                sortOrder: order ? getSortOrder(order) : undefined,
            });
        },
        [updateState],
    );

    useEffect(() => {
        updateState({ query: debouncedQuery });
    }, [debouncedQuery, updateState]);

    return (
        <div className="full-page">
            <PageHeader
                ghost={false}
                title="Destinations"
                extra={
                    <Link
                        to="/destinations/add"
                        className="full-page-header-tools-add-button"
                    >
                        <Button
                            type="primary"
                            size="large"
                            icon={<PlusOutlined />}
                        >
                            Add
                        </Button>
                    </Link>
                }
            >
                <DestinationFilters
                    query={query}
                    source={source}
                    status={status}
                    onlyWithoutPhotos={onlyWithoutPhotos}
                    hasGuide={hasGuide}
                    setDebouncedQuery={setDebouncedQuery}
                    updateState={updateState}
                />
            </PageHeader>
            <div className="full-page-content">
                <div className="max-content-width mb-m">
                    <GenericOverviewTable
                        className={styles.table}
                        columns={columns}
                        apiGetter={apiGetter}
                        emptyTableMessage={
                            query
                                ? 'No destinations found with this text.'
                                : 'Sorry, no destinations yet.'
                        }
                        sortCallback={handleSort}
                        initialCurrentPage={page}
                    ></GenericOverviewTable>
                </div>
            </div>
        </div>
    );
}

type DestinationFiltersProps = {
    query: string;
    source?: number | null;
    status?: number | null;
    onlyWithoutPhotos: boolean;
    hasGuide: boolean;
    setDebouncedQuery: (query: string) => void;
    updateState: (state: Partial<DestinationsListState>) => void;
};

function DestinationFilters({
    query,
    source = null,
    status = null,
    onlyWithoutPhotos,
    hasGuide,
    setDebouncedQuery,
    updateState,
}: DestinationFiltersProps) {
    return (
        <div className={styles.filters}>
            <div className={styles.filter__row}>
                <Search
                    className="full-page-header-tools-filter"
                    placeholder="Search for destinations"
                    size="large"
                    defaultValue={query}
                    onChange={(e) => setDebouncedQuery(e.target.value)}
                />
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Source:</Typography.Text>
                    <Select
                        size="large"
                        defaultValue={null}
                        value={source}
                        onChange={(value) => updateState({ source: value })}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationContentSources.MANUAL}>
                            Manual
                        </Select.Option>
                        <Select.Option value={DestinationContentSources.AI}>
                            AI
                        </Select.Option>
                        <Select.Option
                            value={DestinationContentSources.FROM_GUIDES}
                        >
                            Guides
                        </Select.Option>
                    </Select>
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">Status:</Typography.Text>
                    <Select
                        size="large"
                        defaultValue={null}
                        value={status}
                        onChange={(value) => updateState({ status: value })}
                        className={styles.filter__select}
                    >
                        <Select.Option value={null}>All</Select.Option>
                        <Select.Option value={DestinationStatuses.DRAFT}>
                            Draft
                        </Select.Option>
                        <Select.Option value={DestinationStatuses.PUBLISHED}>
                            Published
                        </Select.Option>
                    </Select>
                </div>
            </div>
            <div className={styles.filter__row}>
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Missing cover image:
                    </Typography.Text>
                    <Switch
                        checked={onlyWithoutPhotos}
                        onChange={(checked) =>
                            updateState({ onlyWithoutPhotos: checked })
                        }
                    />
                </div>
                <Divider type="vertical" />
                <div className={styles.filter__item}>
                    <Typography.Text type="secondary">
                        Has guide:
                    </Typography.Text>
                    <Switch
                        checked={hasGuide}
                        onChange={(checked) =>
                            updateState({ hasGuide: checked })
                        }
                    />
                </div>
            </div>
        </div>
    );
}

function getSortOrder(order: SortOrder | undefined) {
    return order === 'ascend' ? 'asc' : 'desc';
}

type GetGenericTableSortOrderParams = {
    column: string;
    currentSortColumn?: string;
    sortOrder?: string;
};

function getGenericTableSortOrder({
    column,
    currentSortColumn,
    sortOrder,
}: GetGenericTableSortOrderParams) {
    if (currentSortColumn === column) {
        if (!sortOrder) {
            return undefined;
        }

        return sortOrder === 'asc' ? 'ascend' : 'descend';
    }
    return undefined;
}

function getContentSource(destination: DestinationSchema): JSX.Element {
    if (
        !destination.contents ||
        destination.contents.every(
            (content) =>
                (!content.title || content.title.trim() === '') &&
                (!content.body || content.body.trim() === ''),
        )
    ) {
        return <Tag color="gray">No content</Tag>;
    }

    const sources = destination.contents.map((content) => content.source);
    const uniqueSources = [...new Set(sources)];

    const allManualOrGuides = uniqueSources.every(
        (source) =>
            source === DestinationContentSources.MANUAL ||
            source === DestinationContentSources.FROM_GUIDES,
    );

    if (allManualOrGuides) {
        return <Tag color="orange">Manual</Tag>;
    }

    if (
        uniqueSources.length === 1 &&
        uniqueSources[0] === DestinationContentSources.AI
    ) {
        return <Tag color="green">AI</Tag>;
    }

    return <Tag color="purple">Mixed</Tag>;
}
